import React from 'react';

import { Inject } from '@odinjs/odin';

import { phoneNumberFormatter, setVisibility } from '@philips-emr/hpms-components';
import { ButtonAPI, FieldAPI, LinkAPI, PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';

import { DadosContratoAliases, FragmentNames } from '../../codegen';
import { Operadora } from '../../interfaces/operadora-interface';
import { ReportService } from '../../services';
import BaseController from '../base/base-controller';

enum Status {
  SIM = 'S',
  NAO = 'N',
}

@Controller({ fragment: FragmentNames.DADOS_CONTRATO })
export default class DadosContratoController extends BaseController {

  @Inject(DadosContratoAliases.LINK_TELEFONE_OPERADORA)
  private linkTelefoneOperadora: LinkAPI;
  @Inject(DadosContratoAliases.LINK_TELEFONE_ANS)
  private linkTelefoneAns: LinkAPI;
  @Inject(DadosContratoAliases.LINK_EMAIL_ANS)
  private linkEmailAns: LinkAPI;
  @Inject(DadosContratoAliases.LINK_SITE_ANS)
  private linkAns: LinkAPI;
  @Inject(DadosContratoAliases.LINK_NUCLEOS_FISCALIZACAO)
  private linkNucleFiscalizacaoAns: LinkAPI;

  @Inject(DadosContratoAliases.PANEL_COMPARTILHAMENTO_GESTAO_RISCOS)
  private panelRiscos: PanelAPI;

  @Inject(DadosContratoAliases.FIELD_SUB_ESTIPULANTE)
  private fieldSubEstipulante: FieldAPI;
  @Inject(DadosContratoAliases.FIELD_LIMITE_UTILIZACAO)
  private fieldLimiteUtilizacao: FieldAPI;
  @Inject(DadosContratoAliases.FIELD_TITULAR)
  private fieldTitular: FieldAPI;
  @Inject(DadosContratoAliases.FIELD_CONTRATO_COLETIVO)
  private fieldContratoColetivo: FieldAPI;

  @OnMount(DadosContratoAliases.BUTTON_IMPRIMIR)
  onImprimirMount({ target }: MountEvent): void {
    const api = target as ButtonAPI;
    const { nrSeqBeneficiario } = this.context.getBulkData();

    ReportService.print({
      cdGrupo: '4',
      nrSeqBeneficiario: nrSeqBeneficiario as number,
    })
      .then(({ event }) => api.onClick.listen(event))
      .catch(() => api.setVisible(false));
  }

  @OnDynamicContentRender(DadosContratoAliases.CUSTOM_CARD_DADOS_CONTRATO)
  onDynamicContentRender({ data, render }: RenderEvent): void {
    const { ieCumprida } = data as { ieCumprida: Status };

    const props = {
      [Status.SIM]: { icon: StampIcon.CHECK, color: Color.SAFETY_GREEN },
      [Status.NAO]: { icon: StampIcon.CLOCK, color: Color.SAFETY_NEUTRAL },
    };

    render(() => <Stamp {...props[ieCumprida]} label={`beneficiary.carencia-cumprida-${ieCumprida}`.toLowerCase()} />);
  }

  @OnDynamicContentRender(DadosContratoAliases.CUSTOM_CARD_CPT)
  onDynamicContentCptRender({ data, render }: RenderEvent): void {
    const { iePendente } = data as { iePendente: Status };

    const props = {
      [Status.SIM]: { icon: StampIcon.CHECK, color: Color.SAFETY_GREEN },
      [Status.NAO]: { icon: StampIcon.CANCELED_OUTLINE, color: Color.SAFETY_ORANGE },
    };

    render(() => <Stamp {...props[iePendente]} label={`beneficiary.cpt-status-${iePendente}`.toLowerCase()} />);
  }
  @OnAfterFetch(DadosContratoAliases.FETCH_INFORMACOES_CONTRATUAIS)
  onInformacoesAfterFetch(event: FetchEvent): void {
    if (event.data) {
      const data = event.data as { dsSubestipulante: string, dtLimiteUtilizacao: string, dsTitular: string, nrContratoColetivo: string };

      this.fieldSubEstipulante.setVisible(!!data.dsSubestipulante);
      this.fieldLimiteUtilizacao.setVisible(!!data.dtLimiteUtilizacao);
      this.fieldTitular.setVisible(!!data.dsTitular);
      this.fieldContratoColetivo.setVisible(!!data.nrContratoColetivo);
    }
  }

  @OnAfterFetch(DadosContratoAliases.FETCH_COMPARTILHAMENTO_GESTAO_RISCOS)
  onRiscosAfterFetch = (event: FetchEvent): void => setVisibility(event, this.panelRiscos);

  @OnAfterFetch(DadosContratoAliases.FETCH_CARD_OPERADORA)
  onCardOperadoraAfterFetch(event: FetchEvent): void {
    if (event.data) {
      const data = event.data as Operadora;

      const telefoneOperadora = `${data.nrDddTelefoneOperadora}${data.nrTelefoneOperadora}`;
      this.linkTelefoneOperadora.setState({ route: `tel:${telefoneOperadora}` });
      data.nrTelefoneOperadora = phoneNumberFormatter(telefoneOperadora, data.nrDddTelefoneOperadora.startsWith('080') || !data.nrDddTelefoneOperadora) || '';
    }
  }

  @OnAfterFetch(DadosContratoAliases.FETCH_CARD_ANS)
  onCardAnsAfterFetch(event: FetchEvent): void {
    if (event.data) {
      const data = event.data as Operadora;

      this.linkTelefoneAns.setState({ route: `tel:${data.nrTelefoneAns}` });
      data.nrTelefoneAns = phoneNumberFormatter(data.nrTelefoneAns, true);

      this.linkEmailAns.setState({ route: `mailto:${data.dsEmailAns}` });

      data.dsSiteAns && this.linkAns.setState({ route: data.dsSiteAns });
      data.dsLinkNucleoFisc && this.linkNucleFiscalizacaoAns.setState({ route: data.dsLinkNucleoFisc });
    }
  }
}
